import { useState } from 'react';
import { paletteFor } from '../lib/categoryPalette.js';

// Bench card — archive-style horizontal row with click-to-expand for full
// detail (takeaway, summary, draft snippet, tier reason, source link) and
// inline action buttons (Today / Tomorrow / Archive). Uses the shared
// category palette so colors line up with the rest of the editor dashboard.

const TIER_LABEL = {
  1: 'T1 · Raw',
  2: 'T2 · Sorted',
  3: 'T3 · Adjacent',
  4: 'T4 · Shortlist',
  5: 'T5 · Drafted',
  6: 'T6 · Held',
};

function Chevron({ open }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <path d="M3 4.5L6 7.5L9 4.5" />
    </svg>
  );
}

function PromotedBadge({ destination }) {
  const map = {
    today: { label: '✓ On today', bg: 'bg-hxd-ok/15', fg: 'text-hxd-ok', border: 'border-hxd-ok/40' },
    tomorrow: { label: '↣ Tomorrow', bg: 'bg-hxd-orange/15', fg: 'text-hxd-orange', border: 'border-hxd-orange/40' },
    archive: { label: '✕ Archived', bg: 'bg-hxd-muted/10', fg: 'text-hxd-muted', border: 'border-hxd-border' },
  };
  const m = map[destination];
  if (!m) return null;
  return (
    <span
      className={`text-[10px] font-bold uppercase tracking-display-sm px-2 py-0.5 rounded-[2px] border whitespace-nowrap ${m.bg} ${m.fg} ${m.border}`}
    >
      {m.label}
    </span>
  );
}

export default function BenchCard({
  item,
  onPromoteToday,
  onPromoteTomorrow,
  onArchive,
  onUnpromote,
  busy,
}) {
  const [open, setOpen] = useState(false);
  const promoted = item.promoted?.destination;
  const palette = paletteFor(item.category);
  const imageUrl =
    item.pipelineMeta?.image_url || item.pipelineMeta?.imageUrl || null;
  const [imageOk, setImageOk] = useState(Boolean(imageUrl));
  const hasImage = Boolean(imageUrl) && imageOk;

  const sourceUrl = item.sourceUrl || null;
  const preview = item.takeaway || item.summary || item.draftSnippet || '';

  return (
    <div
      className={[
        'group bg-white border border-hxd-border-soft rounded transition-all',
        open ? 'shadow-sm' : 'hover:shadow-sm',
        promoted ? 'opacity-90' : '',
      ].join(' ')}
      style={{
        borderLeftWidth: '3px',
        borderLeftColor: open ? palette.bar : `${palette.bar}80`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex gap-4 p-3 text-left"
      >
        {hasImage ? (
          <div className="hidden sm:block shrink-0 w-28 h-20 rounded overflow-hidden bg-hxd-surface border border-hxd-border-soft">
            <img
              src={imageUrl}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImageOk(false)}
            />
          </div>
        ) : (
          <div
            className="hidden sm:flex shrink-0 w-28 h-20 rounded items-center justify-center"
            style={{ backgroundColor: palette.track }}
          >
            <span
              className="font-display text-[10px] font-bold tracking-display-md uppercase"
              style={{ color: palette.ink }}
            >
              {item.category || '—'}
            </span>
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-hxd-muted mb-1">
            <span
              className="font-display font-bold uppercase tracking-display-sm"
              style={{ color: palette.ink }}
            >
              {item.category || '—'}
            </span>
            <span>·</span>
            <span>{TIER_LABEL[item.tier] || `T${item.tier}`}</span>
            {item.score != null && (
              <>
                <span>·</span>
                <span>score {Math.round(item.score * 100) / 100}</span>
              </>
            )}
            {item.carriedFrom?.isoDate && (
              <>
                <span>·</span>
                <span className="text-hxd-warn">
                  ↩ {item.carriedFrom.isoDate}
                </span>
              </>
            )}
            {promoted && (
              <span className="ml-auto">
                <PromotedBadge destination={promoted} />
              </span>
            )}
          </div>

          <h3
            className="text-[14px] font-bold text-hxd-ink leading-tight m-0"
            style={open ? { color: palette.ink } : undefined}
          >
            {item.title || '(no title)'}
          </h3>

          {!open && preview && (
            <p className="text-[12px] text-hxd-muted mt-1 leading-relaxed m-0 line-clamp-2">
              {preview}
            </p>
          )}

          {!open && item.source && (
            <div className="text-[11px] text-hxd-muted truncate mt-1">
              {item.source}
            </div>
          )}
        </div>

        <div className="shrink-0 self-start text-hxd-muted group-hover:text-hxd-ink mt-1">
          <Chevron open={open} />
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 -mt-1 sm:pl-[8.5rem]">
          {item.takeaway && (
            <p
              className="text-[13px] italic text-hxd-ink pl-3 m-0 leading-relaxed"
              style={{ borderLeft: `2px solid ${palette.bar}` }}
            >
              {item.takeaway}
            </p>
          )}

          {item.summary && (
            <p
              className={`text-[13px] text-[#444] leading-relaxed m-0 ${
                item.takeaway ? 'mt-2' : ''
              }`}
            >
              {item.summary}
            </p>
          )}

          {item.draftSnippet && (
            <div
              className="text-[12px] text-hxd-muted bg-hxd-surface pl-3 py-2 mt-2"
              style={{ borderLeft: `2px solid ${palette.bar}66` }}
            >
              <div
                className="font-display text-[10px] font-bold tracking-display-md uppercase mb-1"
                style={{ color: palette.ink }}
              >
                Pipeline draft snippet
              </div>
              {item.draftSnippet}
            </div>
          )}

          {item.tierReason && (
            <div className="text-[11px] text-hxd-muted italic mt-2">
              Bench reason: {item.tierReason}
            </div>
          )}

          {item.source && (
            <div className="text-[11px] text-hxd-muted mt-2 truncate">
              {item.source}
            </div>
          )}

          <div
            className="flex flex-wrap items-center gap-2 mt-3 pt-3"
            style={{ borderTop: `1px solid #00000010` }}
          >
            {promoted ? (
              <>
                <div className="text-[12px] text-hxd-muted mr-auto">
                  Currently <strong className="text-hxd-ink">{promoted}</strong>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUnpromote?.(item);
                  }}
                  disabled={busy}
                  className="hxd-btn hxd-btn-ghost hxd-btn-sm"
                >
                  Undo
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromoteToday?.(item);
                  }}
                  disabled={busy}
                  className="hxd-btn hxd-btn-primary hxd-btn-sm"
                  title="Drafts the article with the pipeline stage_4_draft prompt, then opens a preview to review before adding to the brief."
                >
                  → Draft &amp; add to Today
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromoteTomorrow?.(item);
                  }}
                  disabled={busy}
                  className="hxd-btn hxd-btn-secondary hxd-btn-sm"
                >
                  ↣ Hold for Tomorrow
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive?.(item);
                  }}
                  disabled={busy}
                  className="hxd-btn hxd-btn-ghost hxd-btn-sm text-hxd-muted"
                >
                  ✕ Archive
                </button>
              </>
            )}

            {sourceUrl && (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="ml-auto text-[12px] text-hxd-orange hover:underline"
              >
                View original →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
